import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "bg-ink-900/60 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-blaze-blueLight h-11";

export function InterestForm({ category, program, onDone, className = "" }) {
  const [form, setForm] = useState({ name: "", parent_name: "", email: "", phone: "", player_age: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/submissions", { category, program, ...form });
      setDone(true);
      toast.success("Request received!", { description: data.message });
    } catch (err) {
      toast.error("Could not send request", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="interest-success" className={`text-center py-8 ${className}`}>
        <CheckCircle2 className="mx-auto text-blaze-orange" size={54} />
        <h3 className="mt-4 font-heading text-3xl tracking-wide text-white">You're On The List!</h3>
        <p className="mt-2 text-white/70 max-w-md mx-auto">
          Thanks for your interest in <span className="text-white font-semibold">{program}</span>. We'll reach out with dates, details and registration info.
        </p>
        <Button
          data-testid="interest-done-btn"
          onClick={() => (onDone ? onDone() : setDone(false))}
          className="mt-6 bg-blaze-blue hover:bg-blaze-blueLight text-white font-bold uppercase tracking-wide rounded-full px-8"
        >
          Done
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="interest-form" className={`space-y-5 ${className}`}>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Player Name *</Label>
          <Input required data-testid="interest-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Player full name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Parent / Guardian Name</Label>
          <Input data-testid="interest-parent" className={inputCls} value={form.parent_name} onChange={set("parent_name")} placeholder="If under 18" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Email *</Label>
          <Input required type="email" data-testid="interest-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Phone</Label>
          <Input data-testid="interest-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(203) 555-0123" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-white/80 text-xs uppercase tracking-wider">Player Age / Grade</Label>
        <Input data-testid="interest-age" className={inputCls} value={form.player_age} onChange={set("player_age")} placeholder="e.g. 12U / 6th grade" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-white/80 text-xs uppercase tracking-wider">Message</Label>
        <Textarea data-testid="interest-message" className="bg-ink-900/60 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-blaze-blueLight min-h-[100px]" value={form.message} onChange={set("message")} placeholder="Anything you'd like us to know." />
      </div>
      <Button
        type="submit"
        disabled={loading}
        data-testid="interest-submit"
        className="w-full bg-blaze-orange hover:bg-blaze-orangeBright text-ink-900 font-bold uppercase tracking-wide rounded-full h-12 text-base shadow-[0_8px_28px_rgba(246,140,31,0.35)] transition-all"
      >
        {loading ? <Loader2 className="animate-spin" /> : "Submit Request"}
      </Button>
      <p className="text-center text-xs text-white/40">
        Inquiry only — we'll confirm dates and registration details by email.
      </p>
    </form>
  );
}
